import { css } from 'lit';

export default css`
  :host {
    display: block;
    font-family: var(--font-family-base, 'Inter, Arial, sans-serif');
    --alert-width: 418px;
    --alert-height: 116px;
    --alert-bg: var(--color-white, #ffffff);
    --alert-border: var(--sm-line, 1.5px) solid;
    --alert-border-radius: var(--radius-md, 12px);
    --icon-size: 16px;
    --title-color: var(--color-dark-gray-2, #030711);
    --description-color: var(--color-gray-6, #7c7c7c);
    --primary-color: var(--color-secondary, #2594e3);
    --secondary-color: var(--color-gray-6, #7c7c7c);
    --state-colors: {
      default: var(--color-primary-light-1, #1d76b5),
      success: var(--color-success, #72b509),
      info: var(--color-secondary, #2594e3),
      warning: var(--color-warning, #ff9505),
      error: var(--color-error, #ff2b32),
    };
  }

  .alert {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: var(--spacing-md, 16px);
    gap: var(--spacing-md, 16px);
    width: var(--alert-width);
    max-width: 90vw;
    height: var(--alert-height);
    border-radius: var(--alert-border-radius);
    box-sizing: border-box;
  }

  :host([disabled]) .alert {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .icon, ::slotted([slot="icon"]) {
    width: var(--icon-size);
    height: var(--icon-size);
    flex: none;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-md, 16px);
    flex: 1;
  }

  .text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-xxs, 4px);
    width: 100%;
  }

  .title, ::slotted([slot="title"]) {
    width: 100%;
    font-size: var(--font-size-s2, 16px);
    line-height: var(--line-height-s2, 24px);
    font-weight: var(--font-weight-semibold, 600);
    color: var(--title-color);
  }

  .description, ::slotted([slot="description"]) {
    width: 100%;
    font-size: var(--font-size-b3, 14px);
    line-height: var(--line-height-b3, 20px);
    font-weight: var(--font-weight-regular, 400);
    color: var(--description-color);
  }

  .buttons {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-md, 16px);
  }

  button, ::slotted([slot="primary-button"]), ::slotted([slot="secondary-button"]) {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    width: auto;
    font-size: var(--font-size-button-large, 16px);
    line-height: var(--line-height-button-large, 20px);
    font-weight: var(--font-weight-semibold, 600);
    text-align: center;
    transition: color 0.2s ease-in-out;
  }

  button:hover:not([disabled]),
  ::slotted([slot="primary-button"]:hover:not([disabled])),
  ::slotted([slot="secondary-button"]:hover:not([disabled])) {
    opacity: 0.8;
  }

  button:focus-visible,
  ::slotted([slot="primary-button"]:focus-visible),
  ::slotted([slot="secondary-button"]:focus-visible) {
    outline: 2px solid var(--color-secondary, #2594e3);
    outline-offset: 2px;
  }

  button[disabled] {
    color: var(--color-gray-3, #d7d4d2);
    cursor: not-allowed;
  }

  /* Outline Style */
  :host([style-type="outline"]) .alert {
    background: var(--alert-bg);
    border: var(--alert-border);
  }

  :host([style-type="outline"][state="default"]) .alert { border-color: var(--color-primary-light-1, #1d76b5); }
  :host([style-type="outline"][state="success"]) .alert { border-color: var(--color-success, #72b509); }
  :host([style-type="outline"][state="info"]) .alert { border-color: var(--color-secondary, #2594e3); }
  :host([style-type="outline"][state="warning"]) .alert { border-color: var(--color-warning, #ff9505); }
  :host([style-type="outline"][state="error"]) .alert { border-color: var(--color-error, #ff2b32); }

  :host([style-type="outline"]) .primary { color: var(--primary-color); }
  :host([style-type="outline"]) .secondary { color: var(--secondary-color); }

  :host([style-type="outline"][state="default"]) .primary { color: var(--color-secondary, #2594e3); }
  :host([style-type="outline"][state="success"]) .primary { color: var(--color-success, #72b509); }
  :host([style-type="outline"][state="info"]) .primary { color: var(--color-secondary, #2594e3); }
  :host([style-type="outline"][state="warning"]) .primary { color: var(--color-warning, #ff9505); }
  :host([style-type="outline"][state="error"]) .primary { color: var(--color-error, #ff2b32); }

  /* Filled Style */
  :host([style-type="filled"]) .alert {
    border: none;
  }

  :host([style-type="filled"][state="default"]) .alert { background: var(--color-primary-light-1, #1d76b5); }
  :host([style-type="filled"][state="success"]) .alert { background: var(--color-success, #72b509); }
  :host([style-type="filled"][state="info"]) .alert { background: var(--color-secondary, #2594e3); }
  :host([style-type="filled"][state="warning"]) .alert { background: var(--color-warning, #ff9505); }
  :host([style-type="filled"][state="error"]) .alert { background: var(--color-error, #ff2b32); }

  :host([style-type="filled"]) .title,
  :host([style-type="filled"]) ::slotted([slot="title"]),
  :host([style-type="filled"]) .description,
  :host([style-type="filled"]) ::slotted([slot="description"]),
  :host([style-type="filled"]) .primary,
  :host([style-type="filled"]) ::slotted([slot="primary-button"]),
  :host([style-type="filled"]) .secondary,
  :host([style-type="filled"]) ::slotted([slot="secondary-button"]) {
    color: var(--color-white, #ffffff);
  }

  :host([style-type="filled"]) .icon,
  :host([style-type="filled"]) ::slotted([slot="icon"]) {
    color: var(--color-white, #ffffff);
  }

  @media (max-width: 768px) {
    .alert {
      width: 100%;
      max-width: 90vw;
      height: auto;
      padding: var(--spacing-sm, 12px);
      gap: var(--spacing-sm, 12px);
    }
    .content {
      gap: var(--spacing-sm, 12px);
    }
    .buttons {
      flex-direction: column;
      gap: var(--spacing-sm, 12px);
      width: 100%;
    }
    button, ::slotted([slot="primary-button"]), ::slotted([slot="secondary-button"]) {
      font-size: var(--font-size-b3, 14px);
    }
  }
`;